/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'clsx';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Fade, IconButton, Modal, ModalProps } from '@mui/material';

import { getOverlayRandomId } from '../../utils/getOverlayRandomId';
import { useEventCallback } from '../hooks/useEventCallback';
import { backdropStyles, createPageOverlayStyles } from './PageOverlay.styles';

type PageOverlayProps = Omit<ModalProps, 'children'> & {
  showCloseBtn?: boolean;
  onCloseBtnClick?: () => any;
  classes?: { childrenBlock?: string; modal?: string };
  stickyButtons?: boolean;
  children: ModalProps['children'] | null;
  name?: string;
};

export function PageOverlay(props: PageOverlayProps) {
  const {
    children,
    open,
    showCloseBtn,
    onCloseBtnClick,
    classes: classNames = {},
    className,
    stickyButtons,
    name,

    ...otherProps
  } = props;
  const [overlayId] = useState(() => getOverlayRandomId(name));
  const [buttonsEl, setButtonsEl] = useState<HTMLElement | null>(null);
  const [modalEl, setModalEl] = useState<HTMLElement | null>(null);
  const [isFixedButtonsBlock, setIsFixedButtonsBlock] = useState<boolean>(false);

  useEffect(() => {
    document.body.classList.toggle(`overlay-open-${overlayId}`, open);
  }, [open, overlayId]);
  const classes = createPageOverlayStyles();

  const fn = useEventCallback(() => {
    const rect = buttonsEl?.getBoundingClientRect();
    if (!rect) {
      return;
    }
    setIsFixedButtonsBlock(rect.top < 15);
  });

  const recalculate = useMemo(() => _.debounce(fn, 5, { maxWait: 10 }), [fn]);

  useEffect(() => {
    if (!open || !stickyButtons || !buttonsEl || !modalEl) {
      setIsFixedButtonsBlock(false);
      return;
    }
    const subscriber = () => {
      recalculate();
    };
    modalEl.addEventListener('scroll', subscriber);
    subscriber();
    return () => modalEl.removeEventListener('scroll', subscriber);
  }, [buttonsEl, modalEl, open, recalculate, stickyButtons]);

  useEffect(() => {
    return () => document.body.classList.remove(`overlay-open-${overlayId}`);
  }, [overlayId]);

  return (
    <Modal
      disableRestoreFocus
      onClose={onCloseBtnClick}
      {...otherProps}
      open={open}
      className={cx(classes.modal, className)}
      slotProps={{ backdrop: { style: backdropStyles, timeout: 500 } }}
      closeAfterTransition
      ref={setModalEl}
      disableEnforceFocus
      disableAutoFocus
      hideBackdrop
    >
      <Fade in={open}>
        <div className={cx(classes.paper, classNames.modal)}>
          {showCloseBtn && (
            <div className={classes.buttonsBlock} ref={setButtonsEl}>
              <div
                className={cx(classes.buttonsBlockInner, {
                  [classes.fixed]: isFixedButtonsBlock,
                })}
              >
                <IconButton
                  aria-label="close"
                  size="medium"
                  color="primary"
                  onClick={onCloseBtnClick}
                  data-cy="page-overlay-close-button"
                >
                  <CloseIcon color="primary" />
                </IconButton>
              </div>
            </div>
          )}
          <div className={cx(classes.childrenBlock, classNames.childrenBlock || '')}>{children}</div>
        </div>
      </Fade>
    </Modal>
  );
}
